import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import {
  RadioGroup,
  RadioGroupItem,
} from '../../../../components/ui/radio-group';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import {
  getStripeSurveyConfig,
  SurveyConfig,
  SurveyResponse,
} from '../../../../services/cancellationServices';

interface StripeSurveyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (surveyResponse: SurveyResponse) => void;
  teamId: string;
}

// Map reason IDs to translation keys
const getReasonTranslationKey = (reasonId: string): string => {
  const mapping: Record<string, string> = {
    missing_language_support: 'missingLanguageSupport',
    found_better_alternative: 'foundBetterAlternative',
    price_too_high: 'priceTooHigh',
    low_usage: 'lowUsage',
    switching_to_different_plan: 'switchingToDifferentPlan',
    difficult_to_use: 'difficultToUse',
    poor_customer_support: 'poorCustomerSupport',
    product_didnt_meet_needs: 'productDidntMeetNeeds',
    other: 'other',
  };
  return mapping[reasonId] || reasonId;
};

export const StripeSurveyModal: React.FC<StripeSurveyModalProps> = ({
  open,
  onClose,
  onSubmit,
  teamId,
}) => {
  const { t } = useTranslation();
  const [surveyConfig, setSurveyConfig] = useState<SurveyConfig | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [feedback, setFeedback] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Get contextual hint based on selected reason
  const getPlaceholder = () => {
    if (!selectedReason)
      return t(
        'settings.billing.cancellation.survey.feedbackPlaceholder',
        'Tell us more...',
      );
    const translationKey = getReasonTranslationKey(selectedReason);
    return t(
      `settings.billing.cancellation.survey.hints.${translationKey}`,
      'Tell us more...',
    );
  };

  const loadSurveyConfig = useCallback(async () => {
    try {
      const config = await getStripeSurveyConfig({ teamId });
      setSurveyConfig(config);
    } catch (error) {
      console.error('Failed to load survey config:', error);
    }
  }, [teamId]);

  useEffect(() => {
    if (open && teamId) {
      loadSurveyConfig();
    }
  }, [open, teamId, loadSurveyConfig]);

  const handleSubmit = () => {
    setIsLoading(true);
    const surveyResponse: SurveyResponse = {
      reason: selectedReason || undefined,
      feedback: feedback.trim() || undefined,
    };
    onSubmit(surveyResponse);
  };

  const handleSkip = () => {
    onSubmit({});
  };

  if (!surveyConfig) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t(
              'settings.billing.cancellation.survey.title',
              "Finally, let us know why you're cancelling",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'settings.billing.cancellation.survey.description',
              'This will help us serve you better in the future.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Survey Reasons */}
          <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
            <div className="space-y-3">
              {surveyConfig.reasons.map((reason) => {
                const translationKey = getReasonTranslationKey(reason.id);
                return (
                  <div key={reason.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={reason.id} id={reason.id} />
                    <Label
                      htmlFor={reason.id}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {t(
                        `settings.billing.cancellation.survey.reasons.${translationKey}`,
                      )}
                    </Label>
                  </div>
                );
              })}
            </div>
          </RadioGroup>

          {/* Additional Feedback */}
          <div className="space-y-2">
            <Label htmlFor="feedback" className="text-sm font-medium">
              {t(
                'settings.billing.cancellation.survey.feedbackLabel',
                'Additional feedback (optional)',
              )}
            </Label>
            <Textarea
              id="feedback"
              placeholder={getPlaceholder()}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={handleSkip} disabled={isLoading}>
            {t('settings.billing.cancellation.survey.skip', 'Skip')}
          </Button>
          <Button variant="default" onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? t(
                  'settings.billing.cancellation.survey.submitting',
                  'Submitting...',
                )
              : t(
                  'settings.billing.cancellation.survey.submit',
                  'Cancel Subscription',
                )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
